'use client'

import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Link } from 'react-router-dom'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console (and eventually to error tracking service like Sentry)
    console.error('Error caught by boundary:', error, errorInfo)
    
    this.setState({
      error,
      errorInfo,
    })

    // In production, send to error tracking service
    if (process.env.NODE_ENV === 'production') {
      // TODO: Send to error tracking service (e.g., Sentry)
      // Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } })
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="relative min-h-screen overflow-hidden bg-stone-50">
          {/* Background layers match primary design */}
          <div className="absolute inset-0 mesh-gradient opacity-90" aria-hidden="true"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/60 to-white/80" aria-hidden="true"></div>

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center min-h-screen">
            <div className="bg-white/85 backdrop-blur-xl border border-red-100 shadow-2xl rounded-3xl p-10 sm:p-12 text-center space-y-6">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-red-50 text-red-600 flex items-center justify-center shadow-inner">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-semibold text-red-600 tracking-wide">Something went wrong</p>
                <h1 className="text-3xl sm:text-4xl font-display font-bold text-stone-900">Oops! An error occurred</h1>
                <p className="text-stone-600 max-w-2xl mx-auto">
                  We're sorry for the inconvenience. The error has been logged and we'll look into it.
                </p>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 text-left bg-stone-50 rounded-lg p-4 border border-stone-200">
                  <summary className="cursor-pointer text-sm font-medium text-stone-700 mb-2">
                    Error Details (Development Only)
                  </summary>
                  <pre className="text-xs text-stone-600 overflow-auto max-h-48">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <button
                  onClick={this.handleReset}
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold shadow-lg shadow-emerald-500/20 hover:bg-emerald-700 transition-all duration-200"
                >
                  Try Again
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-stone-200 text-stone-800 font-semibold hover:border-emerald-300 hover:text-emerald-700 hover:shadow-md transition-all duration-200"
                >
                  Go Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

